"use client"

import { useEffect, useState } from "react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"

interface RoleChangeConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  roleNames: string[]
  /** What applying the preset would overwrite, e.g. ["system prompt", "classification settings"]. */
  overwrites: string[]
  /** Extra consequence of applying the preset (shown only while the checkbox is on). */
  extraWarning?: string
  /** applyPreset: whether the user opted into overwriting with the preset. */
  onConfirm: (applyPreset: boolean) => void
}

/** Role change on an existing agent: metadata-only by default, applying the roles is opt-in. */
export function RoleChangeConfirmDialog({
  open,
  onOpenChange,
  roleNames,
  overwrites,
  extraWarning,
  onConfirm,
}: RoleChangeConfirmDialogProps) {
  const [applyPreset, setApplyPreset] = useState(false)

  useEffect(() => {
    if (open) setApplyPreset(false)
  }, [open])

  const rolesText = roleNames.join(" + ")
  // Two or more roles are merged by AI; one is applied verbatim.
  const applyLabel = roleNames.length > 1 ? `Merge and apply ${rolesText}` : `Also apply the "${rolesText}" preset`

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {roleNames.length > 1 ? `Switch to ${rolesText}?` : `Switch to the "${rolesText}" role?`}
          </AlertDialogTitle>
          <AlertDialogDescription>
            <span>By default only the agent's roles change — the current</span>{" "}
            {overwrites.join(" and ")} <span>stay untouched.</span>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2">
          <div className="flex items-start gap-2">
            <Checkbox
              id="apply-role-preset"
              checked={applyPreset}
              onCheckedChange={(checked) => setApplyPreset(checked === true)}
            />
            <div className="space-y-1">
              <Label htmlFor="apply-role-preset" className="cursor-pointer">
                {applyLabel}
              </Label>
              <p className="text-sm text-muted-foreground">
                <span>Replaces the agent's current</span> {overwrites.join(" and ")} <span>with the roles' defaults.</span>
                <span>{roleNames.length > 1 && " You will review the merged prompt before it is saved."}</span>{" "}
                <span>Manual edits will be lost.</span>
              </p>
            </div>
          </div>
          {applyPreset && extraWarning && (
            <p className="text-sm font-medium text-amber-600">{extraWarning}</p>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => onConfirm(applyPreset)}>
            {roleNames.length > 1 ? "Switch Roles" : "Switch Role"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
